/** Co-branded portfolio gateway: photographic project cards with one action each. */
import { Link } from "react-router-dom";

import { useMe, useProjects } from "../api/hooks";
import { can } from "../components/AppShell";
import { PortfolioCard } from "../components/sat";
import { ErrorNotice, Spinner } from "../components/ui";
import { day, num } from "../lib/format";

export function PortfolioPage() {
  const me = useMe();
  const projects = useProjects();
  const canCreate = can(me.data, "project.edit");
  return (
    <div className="stack" style={{ gap: "1.4rem" }}>
      <div className="row between">
        <div className="sat-landing-brandbar" aria-label="NSGS performance assurance">
          <div className="sat-brand-primary">
            <img className="nsgs" src="/nsgs-logo.png" alt="NSGS" />
            <div className="sat-brand-copy">
              <strong>Performance assurance</strong>
              <span>Commissioning + 15 annual SATs</span>
            </div>
          </div>
          {me.data?.org && (
            <>
              <div className="sat-brand-connector" aria-hidden="true">
                <i />
                <span>for</span>
                <i />
              </div>
              <div className="sat-brand-client">
                <div className="sat-brand-copy">
                  <strong>{String(me.data.org.name)}</strong>
                  <span>Project portfolio</span>
                </div>
              </div>
            </>
          )}
        </div>
        {canCreate && (
          <Link to="/new" className="btn">
            + New project
          </Link>
        )}
      </div>
      <section className="sat-landing-intro">
        <h1>Choose a BESS project</h1>
        <p>Review each site's commissioning test, annual SAT history, and retained evidence.</p>
      </section>
      {projects.isLoading && <Spinner label="Loading projects" />}
      <ErrorNotice error={projects.error} />
      {projects.data && projects.data.length === 0 && (
        <div className="card">
          <h3>No projects yet</h3>
          <p className="muted">
            {canCreate
              ? "Create the first project from its performance guarantee."
              : "No project has been shared with you yet."}
          </p>
        </div>
      )}
      <div className="grid-2" style={{ gap: "1.6rem" }}>
        {projects.data?.map((project) => {
          const saved = project.saved_years ?? [];
          const status = saved.includes(0) ? "Commissioning test recorded" : "Commissioning test not yet recorded";
          const due = project.next_due as { year: number; due_date: string } | null | undefined;
          const detail = due ? `Next annual SAT due ${day(due.due_date)}` : "Open the programme and review the retained record.";
          const capacity = project.capacity_label || (project.nameplate_mwh ? `${num(project.nameplate_mwh, 0)} MWh` : "Capacity not set");
          return (
            <PortfolioCard
              key={project.id}
              title={project.label}
              capacity={capacity}
              status={status}
              detail={detail}
              image={project.card_photo_url}
              action={
                <Link to={`/p/${project.id}`} className="btn primary block" aria-label={`Open project ${project.label}`}>
                  Open project →
                </Link>
              }
            />
          );
        })}
      </div>
      {canCreate && (
        <>
          <div className="sat-landing-divider" />
          <div className="row between">
            <div className="sat-add-project-copy">
              <strong>Add another site</strong>
              <span>Create a new commissioning record and 15-year SAT programme from its performance guarantee.</span>
            </div>
            <Link to="/new" className="btn">
              Create project
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
